import { useEffect, useRef, useState, useCallback } from "react";
import Navbar from "@/components/Navbar";
import { SERVICES } from "@/lib/constants";
import { apiFetch } from "@/lib/api";
import { authClient, syncAuthTokenFromResult } from "@/lib/auth";
import { LogOut, Eye, EyeOff, Save, Plus, Trash2, Edit3, BookOpen, Star, Image, Type, Layout, Upload } from "lucide-react";

type Tab = "text" | "images" | "sections" | "blog" | "reviews" | "account";

interface BlogPost { id: string; title: string; content: string; excerpt: string; thumbnailUrl: string; published: boolean; createdAt: string; }
interface Review { id: string; authorName: string; rating: number; content: string; serviceType: string; published: boolean; createdAt: string; title?: string; thumbnailUrl?: string; }

// ─── Rich Text Editor ────────────────────────────────────────────────────────
interface RichEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
}

function RichEditor({ value, onChange, placeholder }: RichEditorProps) {
  const ref = useRef<HTMLDivElement>(null);
  const isInternal = useRef(false);

  // Sync external value → editor only when not typing
  useEffect(() => {
    if (ref.current && !isInternal.current) {
      if (ref.current.innerHTML !== value) {
        ref.current.innerHTML = value;
      }
    }
    isInternal.current = false;
  }, [value]);

  function exec(cmd: string, val?: string) {
    ref.current?.focus();
    document.execCommand(cmd, false, val);
    handleInput();
  }

  function handleInput() {
    isInternal.current = true;
    onChange(ref.current?.innerHTML ?? "");
  }

  function insertLink() {
    const url = prompt("請輸入連結網址：", "https://");
    if (url) exec("createLink", url);
  }

  const btnClass = "px-2 py-1 text-xs font-semibold border border-gray-300 rounded hover:bg-[#FFE000] hover:border-[#FFE000] transition-colors";

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden focus-within:border-[#FFE000] focus-within:ring-1 focus-within:ring-[#FFE000]">
      {/* Toolbar */}
      <div className="flex flex-wrap gap-1 px-2 py-1.5 bg-gray-50 border-b border-gray-200">
        <button type="button" onClick={() => exec("bold")} className={btnClass} title="粗體"><strong>B</strong></button>
        <button type="button" onClick={() => exec("italic")} className={btnClass} title="斜體"><em>I</em></button>
        <button type="button" onClick={() => exec("formatBlock", "h2")} className={btnClass}>H2</button>
        <button type="button" onClick={() => exec("formatBlock", "h3")} className={btnClass}>H3</button>
        <button type="button" onClick={() => exec("insertUnorderedList")} className={btnClass} title="無序列表">• 列表</button>
        <button type="button" onClick={() => exec("insertOrderedList")} className={btnClass} title="有序列表">1. 列表</button>
        <button type="button" onClick={insertLink} className={btnClass} title="插入連結">🔗 連結</button>
        <button type="button" onClick={() => exec("removeFormat")} className={btnClass} title="清除格式">清除</button>
      </div>
      {/* Editable area */}
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        onBlur={handleInput}
        className="min-h-[180px] px-3 py-2 text-sm focus:outline-none prose prose-sm max-w-none"
        data-placeholder={placeholder}
        style={{ lineHeight: "1.6" }}
      />
    </div>
  );
}

// ─── Image Upload Helper ──────────────────────────────────────────────────────
function compressImage(file: File, maxPx = 900, quality = 0.82): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new window.Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > maxPx || height > maxPx) {
          if (width > height) { height = Math.round((height * maxPx) / width); width = maxPx; }
          else { width = Math.round((width * maxPx) / height); height = maxPx; }
        }
        const canvas = document.createElement("canvas");
        canvas.width = width; canvas.height = height;
        canvas.getContext("2d")!.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.onerror = reject;
      img.src = e.target!.result as string;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/* @section: admin */
export default function AdminPage() {
  const [session, setSession] = useState<{ user: { role?: string } } | null>(null);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [tab, setTab] = useState<Tab>("text");

  const [setupMode, setSetupMode] = useState(false);
  const [setupEmail, setSetupEmail] = useState("");
  const [setupPassword, setSetupPassword] = useState("");
  const [setupName, setSetupName] = useState("");
  const [setupMsg, setSetupMsg] = useState("");

  const [textContent, setTextContent] = useState<Record<string, string>>({});
  const [siteImages, setSiteImages] = useState<Record<string, string>>({});
  const [sections, setSections] = useState<Record<string, boolean>>({});
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [reviews, setReviews] = useState<Review[]>([]);

  const [editTextKey, setEditTextKey] = useState("");
  const [editTextVal, setEditTextVal] = useState("");
  const [editImgKey, setEditImgKey] = useState("");
  const [editImgUrl, setEditImgUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  // Blog form
  const [blogForm, setBlogForm] = useState({ title: "", content: "", excerpt: "", thumbnailUrl: "", published: true, publishedAt: "" });
  const [editBlogId, setEditBlogId] = useState<string | null>(null);
  const [blogPreview, setBlogPreview] = useState(false);
  const blogImgRef = useRef<HTMLInputElement>(null);

  // Review form
  const [reviewForm, setReviewForm] = useState({ title: "", authorName: "", rating: 5, content: "", serviceType: "", published: true, thumbnailUrl: "", publishedAt: "" });
  const [editReviewId, setEditReviewId] = useState<string | null>(null);
  const [reviewPreview, setReviewPreview] = useState(false);
  const reviewImgRef = useRef<HTMLInputElement>(null);

  // Account settings
  const [acctEmail, setAcctEmail] = useState("");
  const [acctCurPwd, setAcctCurPwd] = useState("");
  const [acctNewPwd, setAcctNewPwd] = useState("");
  const [acctMsg, setAcctMsg] = useState("");
  const [acctSaving, setAcctSaving] = useState(false);

  useEffect(() => {
    authClient.getSession().then((s: any) => {
      setSession(s?.data ?? null);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!session) return;
    loadAll();
  }, [session]);

  async function loadAll() {
    try {
      const [t, i, s, b, r] = await Promise.all([
        apiFetch("/content/text").then(r => r.json()),
        apiFetch("/content/images").then(r => r.json()),
        apiFetch("/content/sections").then(r => r.json()),
        apiFetch("/blog/admin/all").then(r => r.json()),
        apiFetch("/reviews/admin/all").then(r => r.json()),
      ]);
      setTextContent(t.data ?? {});
      setSiteImages(i.data ?? {});
      setSections(s.data ?? {});
      setPosts(b.data ?? []);
      setReviews(r.data ?? []);
    } catch(e) { console.error(e); }
  }

  async function login() {
    setLoginError("");
    try {
      const r = await authClient.signIn.email({ email, password } as any);
      if ((r as any).error) { setLoginError("登入失敗：帳號或密碼錯誤"); return; }
      // Save token to sessionStorage so subsequent apiFetch calls include Bearer header
      syncAuthTokenFromResult(r as any);
      const s = await authClient.getSession();
      setSession((s as any)?.data ?? null);
    } catch { setLoginError("登入失敗，請重試"); }
  }

  async function doSetup() {
    setSetupMsg("");
    if (!setupEmail || !setupPassword) { setSetupMsg("請填寫電郵和密碼"); return; }
    try {
      await apiFetch("/admin-setup", {
        auth: false,
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: setupEmail, password: setupPassword, name: setupName || "Admin" })
      });
      setSetupMsg("✅ 管理員帳號已建立！請使用電郵和密碼登入。");
      setEmail(setupEmail); setPassword(setupPassword);
      setTimeout(() => { setSetupMode(false); setSetupMsg(""); }, 2500);
    } catch (e: any) { setSetupMsg(e?.message ?? "設定失敗，帳號可能已存在"); }
  }

  async function logout() {
    await authClient.signOut();
    setSession(null);
  }

  function showMsg(m: string) { setMsg(m); setTimeout(() => setMsg(""), 3000); }

  async function saveText() {
    if (!editTextKey) return;
    setSaving(true);
    await apiFetch(`/content/text/${editTextKey}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ value: editTextVal }) });
    setTextContent(p => ({ ...p, [editTextKey]: editTextVal }));
    setEditTextKey(""); setEditTextVal("");
    setSaving(false); showMsg("已儲存！");
  }

  async function saveImage() {
    if (!editImgKey || !editImgUrl) return;
    setSaving(true);
    await apiFetch(`/content/images/${editImgKey}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ imageUrl: editImgUrl }) });
    setSiteImages(p => ({ ...p, [editImgKey]: editImgUrl }));
    setEditImgKey(""); setEditImgUrl("");
    setSaving(false); showMsg("圖片已更新！");
  }

  async function toggleSection(key: string) {
    const hidden = !sections[key];
    await apiFetch(`/content/sections/${key}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ hidden }) });
    setSections(p => ({ ...p, [key]: hidden }));
    showMsg(`區塊已${hidden ? "隱藏" : "顯示"}！`);
  }

  async function handleBlogImageUpload(file: File) {
    try {
      const dataUrl = await compressImage(file);
      setBlogForm(p => ({ ...p, thumbnailUrl: dataUrl }));
    } catch { showMsg("圖片上傳失敗，請重試"); }
  }

  async function handleReviewImageUpload(file: File) {
    try {
      const dataUrl = await compressImage(file);
      setReviewForm(p => ({ ...p, thumbnailUrl: dataUrl }));
    } catch { showMsg("圖片上傳失敗，請重試"); }
  }

  async function saveBlog() {
    setSaving(true);
    if (editBlogId) {
      const r = await apiFetch(`/blog/${editBlogId}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(blogForm) });
      const d = await r.json();
      setPosts(p => p.map(x => x.id === editBlogId ? d.data : x));
      setEditBlogId(null);
    } else {
      const r = await apiFetch("/blog", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(blogForm) });
      const d = await r.json();
      setPosts(p => [...p, d.data]);
    }
    setBlogForm({ title: "", content: "", excerpt: "", thumbnailUrl: "", published: true, publishedAt: "" });
    setBlogPreview(false);
    setSaving(false); showMsg("博客文章已儲存！");
  }

  async function deleteBlog(id: string) {
    if (!confirm("確定刪除？")) return;
    await apiFetch(`/blog/${id}`, { method: "DELETE" });
    setPosts(p => p.filter(x => x.id !== id));
    showMsg("已刪除");
  }

  async function saveReview() {
    setSaving(true);
    if (editReviewId) {
      const r = await apiFetch(`/reviews/${editReviewId}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(reviewForm) });
      const d = await r.json();
      setReviews(p => p.map(x => x.id === editReviewId ? d.data : x));
      setEditReviewId(null);
    } else {
      const r = await apiFetch("/reviews", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(reviewForm) });
      const d = await r.json();
      setReviews(p => [...p, d.data]);
    }
    setReviewForm({ title: "", authorName: "", rating: 5, content: "", serviceType: "", published: true, thumbnailUrl: "", publishedAt: "" });
    setReviewPreview(false);
    setSaving(false); showMsg("評價已儲存！");
  }

  async function deleteReview(id: string) {
    if (!confirm("確定刪除？")) return;
    await apiFetch(`/reviews/${id}`, { method: "DELETE" });
    setReviews(p => p.filter(x => x.id !== id));
    showMsg("已刪除");
  }

  async function saveAccount() {
    if (!acctNewPwd && !acctEmail) { setAcctMsg("請輸入新電郵或新密碼"); return; }
    if (acctNewPwd && !acctCurPwd) { setAcctMsg("請輸入目前密碼"); return; }
    setAcctSaving(true); setAcctMsg("");
    try {
      await apiFetch("/admin-account", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ newEmail: acctEmail || undefined, newPassword: acctNewPwd || undefined, currentPassword: acctCurPwd || undefined }),
      });
      setAcctMsg("✅ 帳號資料已更新！");
      setAcctEmail(""); setAcctCurPwd(""); setAcctNewPwd("");
    } catch (e: any) { setAcctMsg("❌ 更新失敗：" + (e?.message ?? "請重試")); }
    setAcctSaving(false);
  }

  const allTextKeys = [
    "hero_title","hero_title2","hero_subtitle",
    "services_title","services_subtitle",
    "showcase_title",
    "about_title","about_text","about_point1","about_point2","about_point3","about_point4",
    "contact_title","contact_subtitle",
    ...SERVICES.flatMap(s => [`${s.id}_description`,`${s.id}_subtitle`,`${s.id}_benefits_title`,`${s.id}_cta_title`,`${s.id}_cta_subtitle`])
  ];

  const allImgKeys = [
    "hero_logo","hero_illustration","about_image",
    ...SERVICES.flatMap(s => [`illus_${s.id}`,`icon_${s.id}`])
  ];

  const allSectionKeys = [
    "hero","services","showcase","about","contact",
    ...SERVICES.flatMap(s => [`${s.id}_benefits`,`${s.id}_cta`])
  ];

  if (loading) return <div className="min-h-screen bg-[#0A1628] flex items-center justify-center"><div className="text-[#FFE000] text-xl animate-pulse">載入中...</div></div>;

  if (!session) {
    return (
      <div className="min-h-screen bg-[#0A1628] flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl p-8 w-full max-w-md shadow-2xl">
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold text-[#0A1628]">管理員登入</h1>
            <p className="text-gray-500 text-sm mt-1">YoYo Massage Wellness CMS</p>
          </div>
          {loginError && <p className="text-red-500 text-sm mb-4 bg-red-50 p-3 rounded-lg">{loginError}</p>}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">電子郵件</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-[#FFE000] focus:ring-1 focus:ring-[#FFE000]"
                placeholder="admin@yoyo.hk" onKeyDown={e => e.key === "Enter" && login()} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">密碼</label>
              <input type="password" value={password} onChange={e => setPassword(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-[#FFE000] focus:ring-1 focus:ring-[#FFE000]"
                placeholder="••••••••" onKeyDown={e => e.key === "Enter" && login()} />
            </div>
            <button onClick={login}
              className="w-full py-3 bg-[#FFE000] text-[#0A1628] font-bold rounded-lg hover:bg-yellow-300 transition-colors">
              登入
            </button>
          </div>

          <div className="mt-4 text-center">
            <button onClick={() => { setSetupMode(!setupMode); setSetupMsg(""); }}
              className="text-sm text-[#2E8B57] hover:underline">
              {setupMode ? "← 返回登入" : "首次使用？建立管理員帳號"}
            </button>
          </div>

          {setupMode && (
            <div className="mt-4 border-t pt-4 space-y-3">
              <p className="text-xs text-gray-500 text-center">首次設定：建立管理員帳號（僅限一次）</p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">名稱（可選）</label>
                <input type="text" value={setupName} onChange={e => setSetupName(e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-[#2E8B57] focus:ring-1 focus:ring-[#2E8B57]"
                  placeholder="YoYo Admin" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">電郵</label>
                <input type="email" value={setupEmail} onChange={e => setSetupEmail(e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-[#2E8B57] focus:ring-1 focus:ring-[#2E8B57]"
                  placeholder="admin@yoyo.hk" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">密碼</label>
                <input type="password" value={setupPassword} onChange={e => setSetupPassword(e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-[#2E8B57] focus:ring-1 focus:ring-[#2E8B57]"
                  placeholder="••••••••" onKeyDown={e => e.key === "Enter" && doSetup()} />
              </div>
              {setupMsg && (
                <p className={`text-sm text-center ${setupMsg.startsWith("✅") ? "text-green-600" : "text-red-500"}`}>{setupMsg}</p>
              )}
              <button onClick={doSetup}
                className="w-full py-3 bg-[#2E8B57] text-white font-bold rounded-lg hover:bg-[#1C6034] transition-colors">
                建立管理員帳號
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 font-sans">
      <div className="bg-[#0A1628] text-white px-6 py-4 flex items-center justify-between sticky top-0 z-40 shadow-lg">
        <div>
          <h1 className="font-bold text-lg">YoYo Wellness — CMS 管理面板</h1>
          <p className="text-gray-400 text-xs">內容管理系統</p>
        </div>
        <button onClick={logout} className="flex items-center gap-2 text-gray-400 hover:text-white text-sm transition-colors">
          <LogOut size={15} /> 登出
        </button>
      </div>

      {msg && (
        <div className="fixed top-16 right-4 z-50 bg-green-500 text-white px-4 py-2 rounded-lg shadow-lg text-sm font-medium animate-bounce">
          ✓ {msg}
        </div>
      )}

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-wrap gap-2 mb-8 bg-white rounded-2xl p-2 shadow-sm">
          {([
            { key: "text", label: "文字內容", icon: Type },
            { key: "images", label: "圖片管理", icon: Image },
            { key: "sections", label: "區塊顯示", icon: Layout },
            { key: "blog", label: "博客文章", icon: BookOpen },
            { key: "reviews", label: "客戶評價", icon: Star },
            { key: "account", label: "帳號設定", icon: LogOut },
          ] as const).map(({ key, label, icon: Icon }) => (
            <button key={key} onClick={() => setTab(key as Tab)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${
                tab === key ? "bg-[#0A1628] text-[#FFE000] shadow-md" : "text-gray-600 hover:bg-gray-100"
              }`}>
              <Icon size={15} /> {label}
            </button>
          ))}
        </div>

        {/* ── Text Content Tab ── */}
        {tab === "text" && (
          <div className="space-y-6">
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h2 className="text-lg font-bold text-[#0A1628] mb-4">編輯文字內容</h2>
              <div className="grid sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">選擇內容鍵值</label>
                  <select value={editTextKey} onChange={e => { setEditTextKey(e.target.value); setEditTextVal(textContent[e.target.value] ?? ""); }}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#FFE000]">
                    <option value="">-- 選擇要編輯的內容 --</option>
                    {allTextKeys.map(k => <option key={k} value={k}>{k}</option>)}
                  </select>
                </div>
              </div>
              {editTextKey && (
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">內容（{editTextKey}）</label>
                    <textarea value={editTextVal} onChange={e => setEditTextVal(e.target.value)} rows={4}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#FFE000] resize-y"
                      placeholder="輸入新的內容..." />
                  </div>
                  <button onClick={saveText} disabled={saving}
                    className="flex items-center gap-2 px-5 py-2 bg-[#FFE000] text-[#0A1628] font-semibold rounded-lg hover:bg-yellow-300 transition-colors disabled:opacity-50">
                    <Save size={15} /> {saving ? "儲存中..." : "儲存"}
                  </button>
                </div>
              )}
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h3 className="font-semibold text-[#0A1628] mb-4">目前文字內容（{Object.keys(textContent).length} 項）</h3>
              {Object.keys(textContent).length === 0 ? (
                <p className="text-gray-400 text-sm">尚無自定義文字內容（使用預設值）</p>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {Object.entries(textContent).map(([k, v]) => (
                    <div key={k} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg group">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-mono text-[#0A1628] font-semibold">{k}</p>
                        <p className="text-sm text-gray-600 truncate">{v}</p>
                      </div>
                      <button onClick={() => { setEditTextKey(k); setEditTextVal(v); }}
                        className="text-[#FFE000] opacity-0 group-hover:opacity-100 transition-opacity">
                        <Edit3 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Images Tab ── */}
        {tab === "images" && (
          <div className="space-y-6">
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h2 className="text-lg font-bold text-[#0A1628] mb-4">更換圖片（輸入圖片網址）</h2>
              <div className="grid sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">選擇圖片位置</label>
                  <select value={editImgKey} onChange={e => { setEditImgKey(e.target.value); setEditImgUrl(siteImages[e.target.value] ?? ""); }}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#FFE000]">
                    <option value="">-- 選擇圖片位置 --</option>
                    {allImgKeys.map(k => <option key={k} value={k}>{k}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">新圖片網址（URL）</label>
                  <input type="url" value={editImgUrl} onChange={e => setEditImgUrl(e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#FFE000]"
                    placeholder="https://..." />
                </div>
              </div>
              {editImgUrl && <img src={editImgUrl} alt="預覽" className="h-32 rounded-lg object-cover mb-3 border" />}
              <button onClick={saveImage} disabled={saving || !editImgKey || !editImgUrl}
                className="flex items-center gap-2 px-5 py-2 bg-[#FFE000] text-[#0A1628] font-semibold rounded-lg hover:bg-yellow-300 disabled:opacity-50 transition-colors">
                <Save size={15} /> {saving ? "儲存中..." : "更新圖片"}
              </button>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h3 className="font-semibold text-[#0A1628] mb-4">已自定義的圖片</h3>
              {Object.keys(siteImages).length === 0 ? (
                <p className="text-gray-400 text-sm">尚無自定義圖片（使用預設圖片）</p>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {Object.entries(siteImages).map(([k, url]) => (
                    <div key={k} className="border rounded-xl overflow-hidden group">
                      <img src={url} alt={k} className="w-full h-32 object-cover" />
                      <div className="p-2 bg-gray-50">
                        <p className="text-xs font-mono text-gray-600 truncate">{k}</p>
                        <button onClick={() => { setEditImgKey(k); setEditImgUrl(url); setTab("images"); }}
                          className="text-xs text-[#FFE000] font-medium mt-1 flex items-center gap-1 hover:underline">
                          <Edit3 size={11} /> 編輯
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Sections Tab ── */}
        {tab === "sections" && (
          <div className="bg-white rounded-2xl p-6 shadow-sm">
            <h2 className="text-lg font-bold text-[#0A1628] mb-2">網頁區塊顯示設定</h2>
            <p className="text-gray-500 text-sm mb-6">可隱藏或顯示網頁的各個區塊</p>
            <div className="grid sm:grid-cols-2 gap-3">
              {allSectionKeys.map(key => {
                const isHidden = sections[key] ?? false;
                return (
                  <div key={key} className={`flex items-center justify-between p-4 rounded-xl border-2 transition-colors ${isHidden ? "border-red-200 bg-red-50" : "border-green-200 bg-green-50"}`}>
                    <div>
                      <p className="font-mono text-sm font-semibold text-[#0A1628]">{key}</p>
                      <p className="text-xs mt-0.5" style={{ color: isHidden ? "#ef4444" : "#16a34a" }}>
                        {isHidden ? "已隱藏" : "正在顯示"}
                      </p>
                    </div>
                    <button onClick={() => toggleSection(key)}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        isHidden ? "bg-green-500 text-white hover:bg-green-600" : "bg-red-500 text-white hover:bg-red-600"
                      }`}>
                      {isHidden ? <Eye size={14} /> : <EyeOff size={14} />}
                      {isHidden ? "顯示" : "隱藏"}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── Blog Tab ── */}
        {tab === "blog" && (
          <div className="space-y-6">
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-[#0A1628]">{editBlogId ? "編輯文章" : "新增博客文章"}</h2>
                <button onClick={() => setBlogPreview(p => !p)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${blogPreview ? "bg-[#0A1628] text-[#FFE000] border-[#0A1628]" : "border-gray-300 text-gray-600 hover:bg-gray-50"}`}>
                  <Eye size={13} /> {blogPreview ? "關閉預覽" : "👁 預覽"}
                </button>
              </div>

              {blogPreview ? (
                /* Live preview */
                <div className="border border-gray-200 rounded-xl p-5 bg-gray-50 min-h-[200px]">
                  <h2 className="text-xl font-bold text-[#0A1628] mb-2">{blogForm.title || "(無標題)"}</h2>
                  {blogForm.thumbnailUrl && <img src={blogForm.thumbnailUrl} alt="封面" className="w-full max-h-64 object-cover rounded-lg mb-3" />}
                  {blogForm.excerpt && <p className="text-gray-500 italic text-sm mb-3">{blogForm.excerpt}</p>}
                  <div className="prose prose-sm max-w-none text-gray-700" dangerouslySetInnerHTML={{ __html: blogForm.content || "<p class='text-gray-400'>（無內容）</p>" }} />
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">標題 *</label>
                      <input value={blogForm.title} onChange={e => setBlogForm(p => ({ ...p, title: e.target.value }))}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#FFE000]"
                        placeholder="文章標題" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">封面圖片</label>
                      <div className="flex gap-2">
                        <input value={blogForm.thumbnailUrl.startsWith("data:") ? "" : blogForm.thumbnailUrl}
                          onChange={e => setBlogForm(p => ({ ...p, thumbnailUrl: e.target.value }))}
                          className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#FFE000]"
                          placeholder="https://... 或上傳圖片" />
                        <button type="button" onClick={() => blogImgRef.current?.click()}
                          className="flex items-center gap-1 px-3 py-2 bg-[#2E8B57] text-white text-sm rounded-lg hover:bg-[#1C6034] transition-colors whitespace-nowrap">
                          <Upload size={13} /> 上傳
                        </button>
                        <input ref={blogImgRef} type="file" accept="image/*" className="hidden"
                          onChange={e => e.target.files?.[0] && handleBlogImageUpload(e.target.files[0])} />
                      </div>
                      {blogForm.thumbnailUrl && (
                        <img src={blogForm.thumbnailUrl} alt="封面預覽" className="mt-2 h-20 rounded-lg object-cover border" />
                      )}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">摘要</label>
                    <input value={blogForm.excerpt} onChange={e => setBlogForm(p => ({ ...p, excerpt: e.target.value }))}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#FFE000]"
                      placeholder="文章摘要（選填）" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">文章日期（可自訂，留空則使用建立日期）</label>
                    <input type="date" value={blogForm.publishedAt} onChange={e => setBlogForm(p => ({ ...p, publishedAt: e.target.value }))}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#FFE000]" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">內容 *</label>
                    <RichEditor
                      value={blogForm.content}
                      onChange={html => setBlogForm(p => ({ ...p, content: html }))}
                      placeholder="文章內容..."
                    />
                  </div>
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={blogForm.published} onChange={e => setBlogForm(p => ({ ...p, published: e.target.checked }))}
                        className="w-4 h-4 accent-[#FFE000]" />
                      <span className="text-sm text-gray-700">立即發布</span>
                    </label>
                  </div>
                </div>
              )}

              <div className="flex gap-3 mt-4">
                <button onClick={saveBlog} disabled={saving || !blogForm.title || !blogForm.content}
                  className="flex items-center gap-2 px-5 py-2 bg-[#FFE000] text-[#0A1628] font-semibold rounded-lg hover:bg-yellow-300 disabled:opacity-50 transition-colors">
                  <Save size={15} /> {saving ? "儲存中..." : (editBlogId ? "更新文章" : "發布文章")}
                </button>
                {editBlogId && (
                  <button onClick={() => { setEditBlogId(null); setBlogForm({ title:"",content:"",excerpt:"",thumbnailUrl:"",published:true,publishedAt:"" }); setBlogPreview(false); }}
                    className="px-5 py-2 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 text-sm">取消</button>
                )}
              </div>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h3 className="font-semibold text-[#0A1628] mb-4">所有文章（{posts.length} 篇）</h3>
              {posts.length === 0 ? <p className="text-gray-400 text-sm">尚無文章</p> : (
                <div className="space-y-3">
                  {posts.map(p => (
                    <div key={p.id} className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl group">
                      {p.thumbnailUrl && <img src={p.thumbnailUrl} alt={p.title} className="w-14 h-14 object-cover rounded-lg flex-shrink-0" />}
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-[#0A1628] text-sm truncate">{p.title}</p>
                        <p className="text-xs text-gray-400">{new Date(p.createdAt).toLocaleDateString("zh-HK")} · {p.published ? "已發布" : "草稿"}</p>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => { setEditBlogId(p.id); setBlogForm({ title:p.title,content:p.content,excerpt:p.excerpt,thumbnailUrl:p.thumbnailUrl,published:p.published,publishedAt:(p as any).publishedAt||'' }); setBlogPreview(false); }}
                          className="p-1.5 text-[#0A1628] hover:bg-[#FFE000]/20 rounded-lg"><Edit3 size={14} /></button>
                        <button onClick={() => deleteBlog(p.id)}
                          className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg"><Trash2 size={14} /></button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Account Tab ── */}
        {tab === "account" && (
          <div className="bg-white rounded-2xl p-6 shadow-sm max-w-lg">
            <h2 className="text-lg font-bold text-[#0A1628] mb-6">帳號設定</h2>
            {acctMsg && (
              <div className={`mb-4 p-3 rounded-lg text-sm font-medium ${acctMsg.startsWith("✅") ? "bg-green-50 text-green-700" : "bg-red-50 text-red-600"}`}>
                {acctMsg}
              </div>
            )}
            <div className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">更改登入電郵</label>
                <input type="text" value={acctEmail} onChange={e => setAcctEmail(e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#FFE000]"
                  placeholder="新電郵（例如 yoyo@wellness）" />
              </div>
              <div className="border-t pt-5">
                <p className="text-sm font-semibold text-gray-700 mb-3">更改密碼</p>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">目前密碼</label>
                    <input type="password" value={acctCurPwd} onChange={e => setAcctCurPwd(e.target.value)}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#FFE000]"
                      placeholder="••••••••" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">新密碼</label>
                    <input type="password" value={acctNewPwd} onChange={e => setAcctNewPwd(e.target.value)}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#FFE000]"
                      placeholder="••••••••（至少 8 位）" />
                  </div>
                </div>
              </div>
              <button onClick={saveAccount} disabled={acctSaving}
                className="flex items-center gap-2 px-5 py-2.5 bg-[#FFE000] text-[#0A1628] font-semibold rounded-lg hover:bg-yellow-300 disabled:opacity-50 transition-colors">
                <Save size={15} /> {acctSaving ? "儲存中..." : "儲存變更"}
              </button>
            </div>
          </div>
        )}

        {/* ── Reviews Tab ── */}
        {tab === "reviews" && (
          <div className="space-y-6">
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-[#0A1628]">{editReviewId ? "編輯評價" : "新增客戶評價"}</h2>
                <button onClick={() => setReviewPreview(p => !p)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${reviewPreview ? "bg-[#0A1628] text-[#FFE000] border-[#0A1628]" : "border-gray-300 text-gray-600 hover:bg-gray-50"}`}>
                  <Eye size={13} /> {reviewPreview ? "關閉預覽" : "👁 預覽"}
                </button>
              </div>

              {reviewPreview ? (
                /* Live preview for review */
                <div className="border border-gray-200 rounded-xl overflow-hidden bg-white shadow-sm">
                  {reviewForm.thumbnailUrl && (
                    <img src={reviewForm.thumbnailUrl} alt="封面" className="w-full h-48 object-cover" />
                  )}
                  <div className="p-5">
                    <h3 className="text-lg font-bold text-[#0A1628] mb-1">{reviewForm.title || reviewForm.authorName + "的評價"}</h3>
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-[#FFE000] text-sm">{"★".repeat(reviewForm.rating)}{"☆".repeat(5-reviewForm.rating)}</span>
                      <span className="text-xs text-gray-500">{reviewForm.authorName || "（客戶名稱）"}</span>
                      {reviewForm.serviceType && <span className="text-xs bg-[#2E8B57] text-white px-2 py-0.5 rounded-full">{reviewForm.serviceType}</span>}
                    </div>
                    <div className="prose prose-sm max-w-none text-gray-700" dangerouslySetInnerHTML={{ __html: reviewForm.content || "<p class='text-gray-400'>（無內容）</p>" }} />
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">評價標題</label>
                      <input value={reviewForm.title} onChange={e => setReviewForm(p => ({ ...p, title: e.target.value }))}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#FFE000]"
                        placeholder="例如：放鬆了整個身體！" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">客戶名稱（選填）</label>
                      <input value={reviewForm.authorName} onChange={e => setReviewForm(p => ({ ...p, authorName: e.target.value }))}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#FFE000]"
                        placeholder="例如：陳先生（留空則顯示匿名客戶）" />
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">服務項目</label>
                      <select value={reviewForm.serviceType} onChange={e => setReviewForm(p => ({ ...p, serviceType: e.target.value }))}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#FFE000]">
                        <option value="">-- 選擇服務 --</option>
                        {SERVICES.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">封面圖片</label>
                      <div className="flex gap-2">
                        <input value={reviewForm.thumbnailUrl.startsWith("data:") ? "" : reviewForm.thumbnailUrl}
                          onChange={e => setReviewForm(p => ({ ...p, thumbnailUrl: e.target.value }))}
                          className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#FFE000]"
                          placeholder="https://... 或上傳圖片" />
                        <button type="button" onClick={() => reviewImgRef.current?.click()}
                          className="flex items-center gap-1 px-3 py-2 bg-[#2E8B57] text-white text-sm rounded-lg hover:bg-[#1C6034] transition-colors whitespace-nowrap">
                          <Upload size={13} /> 上傳
                        </button>
                        <input ref={reviewImgRef} type="file" accept="image/*" className="hidden"
                          onChange={e => e.target.files?.[0] && handleReviewImageUpload(e.target.files[0])} />
                      </div>
                      {reviewForm.thumbnailUrl && (
                        <img src={reviewForm.thumbnailUrl} alt="封面預覽" className="mt-2 h-20 rounded-lg object-cover border" />
                      )}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">評分</label>
                    <div className="flex gap-2">
                      {[1,2,3,4,5].map(i => (
                        <button key={i} type="button" onClick={() => setReviewForm(p => ({ ...p, rating: i }))}
                          className={`text-2xl transition-transform hover:scale-110 ${i <= reviewForm.rating ? "text-[#FFE000]" : "text-gray-300"}`}>★</button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">評價日期（可自訂，留空則使用建立日期）</label>
                    <input type="date" value={reviewForm.publishedAt} onChange={e => setReviewForm(p => ({ ...p, publishedAt: e.target.value }))}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#FFE000]" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">評價內容 *</label>
                    <RichEditor
                      value={reviewForm.content}
                      onChange={html => setReviewForm(p => ({ ...p, content: html }))}
                      placeholder="客戶評語..."
                    />
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={reviewForm.published} onChange={e => setReviewForm(p => ({ ...p, published: e.target.checked }))}
                      className="w-4 h-4 accent-[#FFE000]" />
                    <span className="text-sm text-gray-700">立即顯示</span>
                  </label>
                </div>
              )}

              <div className="flex gap-3 mt-4">
                <button onClick={saveReview} disabled={saving || !reviewForm.content}
                  className="flex items-center gap-2 px-5 py-2 bg-[#FFE000] text-[#0A1628] font-semibold rounded-lg hover:bg-yellow-300 disabled:opacity-50 transition-colors">
                  <Save size={15} /> {saving ? "儲存中..." : (editReviewId ? "更新評價" : "新增評價")}
                </button>
                {editReviewId && (
                  <button onClick={() => { setEditReviewId(null); setReviewForm({ title:"",authorName:"",rating:5,content:"",serviceType:"",published:true,thumbnailUrl:"",publishedAt:"" }); setReviewPreview(false); }}
                    className="px-5 py-2 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 text-sm">取消</button>
                )}
              </div>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h3 className="font-semibold text-[#0A1628] mb-4">所有評價（{reviews.length} 個）</h3>
              {reviews.length === 0 ? <p className="text-gray-400 text-sm">尚無評價</p> : (
                <div className="space-y-3">
                  {reviews.map(r => (
                    <div key={r.id} className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl group">
                      {r.thumbnailUrl && <img src={r.thumbnailUrl} alt="封面" className="w-14 h-14 object-cover rounded-lg flex-shrink-0" />}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-semibold text-[#0A1628] text-sm">{r.title || r.authorName}</p>
                          <span className="text-[#FFE000] text-xs">{"★".repeat(r.rating)}</span>
                          {r.serviceType && <span className="text-xs bg-gray-200 px-2 py-0.5 rounded-full">{r.serviceType}</span>}
                        </div>
                        <p className="text-xs text-gray-500 truncate mt-0.5" dangerouslySetInnerHTML={{ __html: r.content }} />
                        <p className="text-xs text-gray-400">{r.published ? "已顯示" : "隱藏中"} · {r.authorName}</p>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => {
                          setEditReviewId(r.id);
                          setReviewForm({ title:r.title||"",authorName:r.authorName,rating:r.rating,content:r.content,serviceType:r.serviceType||"",published:r.published,thumbnailUrl:r.thumbnailUrl||"",publishedAt:(r as any).publishedAt||"" });
                          setReviewPreview(false);
                        }} className="p-1.5 text-[#0A1628] hover:bg-[#FFE000]/20 rounded-lg"><Edit3 size={14} /></button>
                        <button onClick={() => deleteReview(r.id)}
                          className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg"><Trash2 size={14} /></button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
